import { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import z from 'zod'
import { toast } from 'sonner'
import {
    Building2,
    Plus,
    Edit,
    Trash2,
    ToggleLeft,
    ToggleRight,
    Search,
    MapPin,
    Phone,
    Mail,
    Check,
    AlertCircle
} from 'lucide-react'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { Badge } from '../components/ui/badge'
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from '../components/ui/dialog'
import { FormError } from '../components/ui/FormError'
import { TableSkeleton } from '../components/ui/table-skeleton'
import { GlobalPaginator } from '../components/ui/global-paginator'
import { Spinner } from '@/app/lib/loader/loader'
import { sucursalService, Sucursal } from '../services/sucursal.service'

// ─── Esquemas de validación ─────────────────────────────────────────────────

const sucursalSchema = z.object({
    name: z.string().min(1, 'El nombre es requerido'),
    address: z.string().optional(),
    phone_number: z.string().optional(),
    email: z.union([z.literal(''), z.string().email('Debes ingresar un correo válido')]).optional(),
    is_active: z.boolean(),
})

type SucursalFormValidate = z.infer<typeof sucursalSchema>

// ─── Component ────────────────────────────────────────────────────────────────

export function Sucursals() {
    const [sucursales, setSucursales] = useState<Sucursal[]>([])
    const [isLoading, setIsLoading] = useState(true)
    const [searchQuery, setSearchQuery] = useState('')

    // Paginación
    const [pagination, setPagination] = useState({
        currentPage: 1,
        total: 0,
        perPage: 10,
        lastPage: 1,
        from: 0,
        to: 0,
    })

    // Modals state
    const [showModal, setShowModal] = useState(false)
    const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null)
    const [editingId, setEditingId] = useState<number | null>(null)

    const {
        register,
        handleSubmit,
        setValue,
        watch,
        reset,
        formState: { errors, isSubmitting }
    } = useForm<SucursalFormValidate>({
        resolver: zodResolver(sucursalSchema),
        defaultValues: {
            name: '',
            address: '',
            phone_number: '',
            email: '',
            is_active: true,
        }
    })

    const isActiveValue = watch('is_active')

    // ─── Handlers ───────────────────────────────────────────────────────────────

    const fetchSucursales = async (page = 1, search = searchQuery) => {
        setIsLoading(true)
        try {
            const res = await sucursalService.getSucursales({ 
                company_id: 1,
                page,
                per_page: pagination.perPage,
                search,
            })
            const paginator = res.data

            setSucursales(paginator.data)

            setPagination({
                currentPage: paginator.current_page,
                total: paginator.total,
                perPage: paginator.per_page,
                lastPage: paginator.last_page,
                from: paginator.from,
                to: paginator.to,
            })
        } catch (error) {
            toast.error('Error al cargar las sucursales')
        } finally {
            setIsLoading(false)
        }
    }

    useEffect(() => {
        fetchSucursales(pagination.currentPage, searchQuery)
    }, [pagination.currentPage, searchQuery])

    const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchQuery(e.target.value)
        setPagination(prev => ({
            ...prev,
            currentPage: 1,
        }))
    }

    const openCreateModal = () => {
        setEditingId(null)
        reset({
            name: '',
            address: '',
            phone_number: '',
            email: '',
            is_active: true,
        })
        setShowModal(true)
    }

    const openEditModal = (sucursal: Sucursal) => {
        setEditingId(sucursal.id)
        reset({
            name: sucursal.name,
            address: sucursal.address || '',
            phone_number: sucursal.phone_number || '',
            email: sucursal.email || '',
            is_active: sucursal.is_active,
        })
        setShowModal(true)
    }

    const handleCloseModal = () => {
        setShowModal(false)
        setEditingId(null)
        reset()
    }

    const onSubmit = async (data: SucursalFormValidate) => {
        try {
            const payload = {
                ...data,
                address: data.address || null,
                phone_number: data.phone_number || null,
                email: data.email || null,
            }

            if (editingId) {
                await sucursalService.updateSucursal(editingId, payload)
                toast.success('Sucursal actualizada exitosamente')
            } else {
                await sucursalService.createSucursal({ company_id: 1, ...payload })
                toast.success('Sucursal creada exitosamente')
            }
            handleCloseModal()
            fetchSucursales()
        } catch (error) {
            toast.error('Ocurrió un error al guardar la sucursal')
        }
    }

    const handleDelete = async (id: number) => {
        try {
            await sucursalService.deleteSucursal(id)
            toast.success('Sucursal eliminada exitosamente')
            setDeleteConfirmId(null)
            fetchSucursales()
        } catch (error) {
            toast.error('Error al eliminar la sucursal')
        }
    }

    const toggleStatus = async (sucursal: Sucursal) => {
        try {
            await sucursalService.updateSucursal(sucursal.id, { is_active: !sucursal.is_active })
            toast.success(`Sucursal ${sucursal.is_active ? 'desactivada' : 'activada'}`)
            fetchSucursales()
        } catch (error) {
            toast.error('Error al cambiar el estado de la sucursal')
        }
    }

    // ─── Render ──────────────────────────────────────────────────────────────────

    return (
        <div className="space-y-6 p-6">
            {/* Header & Filters */}
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                    <h1 className="text-2xl font-semibold text-slate-900">Sucursales</h1>
                    <p className="mt-1 text-sm text-slate-600">
                        Administra las sucursales y puntos de venta de la empresa.
                    </p>
                </div>
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                        <Input
                            placeholder="Buscar sucursal..."
                            value={searchQuery}
                            onChange={handleSearch}
                            className="w-full pl-9 sm:w-64"
                        />
                    </div>
                    <Button
                        onClick={openCreateModal}
                        className="bg-[#3B7D3B] hover:bg-[#2d6a2d] w-full sm:w-auto"
                    >
                        <Plus className="mr-2 h-4 w-4" />
                        Nueva Sucursal
                    </Button>
                </div>
            </div>

            {/* Table */}
            {isLoading ? (
                <TableSkeleton
                    headers={['Sucursal', 'Contacto', 'Fecha Registro', 'Estado', 'Acciones']}
                    columns={['avatar-text', 'text-two-lines', 'text', 'badge', 'actions']}
                    rows={5}
                />
            ) : (
                <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
                    <div className="overflow-x-auto">
                        <table className="w-full whitespace-nowrap">
                            <thead className="border-b border-slate-200 bg-slate-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">
                                        Sucursal
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">
                                        Contacto
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">
                                        Fecha Registro
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">
                                        Estado
                                    </th>
                                    <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-slate-500">
                                        Acciones
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-200">
                                {sucursales.length > 0 ? (
                                    sucursales.map((sucursal) => (
                                        <tr key={sucursal.id} className="transition-colors hover:bg-slate-50/50">
                                            <td className="px-6 py-4">
                                                <div className="flex items-center gap-3">
                                                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-green-100 text-green-700">
                                                        <Building2 className="h-5 w-5" />
                                                    </div>
                                                    <div>
                                                        <p className="font-medium text-slate-900">{sucursal.name}</p>
                                                        <p className="flex items-center gap-1 text-sm text-slate-500">
                                                            <MapPin className="h-3 w-3" />
                                                            {sucursal.address || 'Sin dirección'}
                                                        </p>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="flex flex-col gap-1 text-sm text-slate-600">
                                                    {sucursal.email && (
                                                        <span className="flex items-center gap-1.5">
                                                            <Mail className="h-3.5 w-3.5 text-slate-400" />
                                                            {sucursal.email}
                                                        </span>
                                                    )}
                                                    {sucursal.phone_number && (
                                                        <span className="flex items-center gap-1.5">
                                                            <Phone className="h-3.5 w-3.5 text-slate-400" />
                                                            {sucursal.phone_number}
                                                        </span>
                                                    )}
                                                    {!sucursal.email && !sucursal.phone_number && (
                                                        <span className="text-slate-400 italic">Sin contacto</span>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 text-sm text-slate-600">
                                                {sucursal.created_at ? sucursal.created_at.split('T')[0] : ''}
                                            </td>
                                            <td className="px-6 py-4">
                                                <Badge
                                                    className={
                                                        sucursal.is_active
                                                            ? 'bg-green-100 text-green-700 hover:bg-green-100'
                                                            : 'bg-slate-100 text-slate-700 hover:bg-slate-100'
                                                    }
                                                >
                                                    {sucursal.is_active ? 'Activa' : 'Inactiva'}
                                                </Badge>
                                            </td>
                                            <td className="px-6 py-4 text-right">
                                                <div className="flex items-center justify-end gap-2">
                                                    <button
                                                        onClick={() => toggleStatus(sucursal)}
                                                        title={sucursal.is_active ? 'Desactivar' : 'Activar'}
                                                        className={`rounded p-1.5 transition-colors ${sucursal.is_active
                                                            ? 'text-green-600 hover:bg-green-50'
                                                            : 'text-slate-400 hover:bg-slate-100'
                                                            }`}
                                                    >
                                                        {sucursal.is_active ? (
                                                            <ToggleRight className="h-5 w-5" />
                                                        ) : (
                                                            <ToggleLeft className="h-5 w-5" />
                                                        )}
                                                    </button>
                                                    <button
                                                        onClick={() => openEditModal(sucursal)}
                                                        title="Editar"
                                                        className="rounded p-1.5 text-slate-600 transition-colors hover:bg-slate-100"
                                                    >
                                                        <Edit className="h-4 w-4" />
                                                    </button>
                                                    <button
                                                        onClick={() => setDeleteConfirmId(sucursal.id)}
                                                        title="Eliminar"
                                                        className="rounded p-1.5 text-red-600 transition-colors hover:bg-red-50"
                                                    >
                                                        <Trash2 className="h-4 w-4" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan={5} className="px-6 py-12 text-center">
                                            <Building2 className="mx-auto mb-3 h-8 w-8 text-slate-300" />
                                            <p className="text-slate-500">No se encontraron sucursales</p>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                    {/* Controles de Paginacion */}
                    {sucursales.length > 0 && (
                        <GlobalPaginator
                            totalItems={pagination.total}
                            itemsPerPage={pagination.perPage}
                            currentPage={pagination.currentPage}
                            from={pagination.from}
                            to={pagination.to}
                            onPageChange={(page) =>
                                setPagination(prev => ({
                                    ...prev,
                                    currentPage: page,
                                }))
                            }
                        />
                    )}
                </div>
            )}

            {/* ═══════════════════════════════════════════════════════════
          MODAL: Crear / Editar Sucursal
      ══════════════════════════════════════════════════════════════ */}
            <Dialog open={showModal} onOpenChange={setShowModal}>
                <DialogContent className="sm:max-w-md">
                    <form onSubmit={handleSubmit(onSubmit)}>
                        <DialogHeader>
                            <DialogTitle className="flex items-center gap-2 text-lg font-semibold text-slate-900">
                                <Building2 className="h-5 w-5 text-[#3B7D3B]" />
                                {editingId ? 'Editar Sucursal' : 'Nueva Sucursal'}
                            </DialogTitle>
                            <DialogDescription>
                                {editingId
                                    ? 'Modifica los datos de la sucursal existente.'
                                    : 'Ingresa los datos para registrar un nuevo punto de venta.'}
                            </DialogDescription>
                        </DialogHeader>

                        <div className="space-y-4 py-4">
                            <div className="space-y-2">
                                <label className="text-sm font-medium text-slate-700">
                                    Nombre de la sucursal <span className="text-red-500">*</span>
                                </label>
                                <Input
                                    placeholder="Ej. Sucursal Centro"
                                    {...register('name')}
                                />
                                <FormError message={errors.name?.message} />
                            </div>

                            <div className="space-y-2">
                                <label className="text-sm font-medium text-slate-700">
                                    Dirección
                                </label>
                                <Input
                                    placeholder="Calle, número, colonia..."
                                    {...register('address')}
                                />
                                <FormError message={errors.address?.message} />
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <label className="text-sm font-medium text-slate-700">
                                        Teléfono
                                    </label>
                                    <Input
                                        placeholder="Ej. 6671234567"
                                        {...register('phone_number')}
                                    />
                                    <FormError message={errors.phone_number?.message} />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-sm font-medium text-slate-700">
                                        Correo electrónico
                                    </label>
                                    <Input
                                        type="email"
                                        placeholder="contacto@sucursal.com"
                                        {...register('email')}
                                    />
                                    <FormError message={errors.email?.message} />
                                </div>
                            </div>

                            <div className="flex items-center gap-2 pt-2">
                                <button
                                    type="button"
                                    role="switch"
                                    aria-checked={isActiveValue}
                                    onClick={() => setValue('is_active', !isActiveValue)}
                                    className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer items-center justify-center rounded-full transition-colors duration-200 ease-in-out focus:outline-none ${isActiveValue ? 'bg-green-500' : 'bg-slate-200'
                                        }`}
                                >
                                    <span
                                        aria-hidden="true"
                                        className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${isActiveValue ? 'translate-x-2' : '-translate-x-2'
                                            }`}
                                    />
                                </button>
                                <span className="text-sm text-slate-700">Sucursal Activa</span>
                            </div>
                        </div>

                        <DialogFooter className="gap-2 sm:justify-end">
                            <Button type="button" variant="outline" onClick={handleCloseModal} disabled={isSubmitting}>
                                Cancelar
                            </Button>
                            <Button
                                type="submit"
                                disabled={isSubmitting}
                                className="bg-[#3B7D3B] hover:bg-[#2d6a2d]"
                            >
                                {isSubmitting ? (
                                    <Spinner className="mr-2 h-4 w-4" size="sm" />
                                ) : (
                                    <Check className="mr-2 h-4 w-4" />
                                )}
                                Guardar
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* ═══════════════════════════════════════════════════════════
          MODAL: Confirmar Eliminación
      ══════════════════════════════════════════════════════════════ */}
            <Dialog
                open={deleteConfirmId !== null}
                onOpenChange={(open) => !open && setDeleteConfirmId(null)}
            >
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-lg font-semibold text-red-600">
                            <AlertCircle className="h-5 w-5" />
                            Eliminar Sucursal
                        </DialogTitle>
                        <DialogDescription className="pt-2">
                            ¿Estás seguro de que deseas eliminar esta sucursal? Esta acción no
                            se puede deshacer y podría afectar los registros asociados a ella.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter className="mt-4 gap-2 sm:justify-end">
                        <Button
                            variant="outline"
                            onClick={() => setDeleteConfirmId(null)}
                        >
                            Cancelar
                        </Button>
                        <Button
                            variant="destructive"
                            onClick={() => deleteConfirmId && handleDelete(deleteConfirmId)}
                        >
                            Sí, eliminar
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    )
}
